(() => {
  if (customElements.get('customer-ugc-reviews')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CustomerUgcReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.customer-ugc-reviews__track');
      this.slides = Array.from(this.querySelectorAll('.customer-ugc-reviews__slide'));
      this.dots = Array.from(this.querySelectorAll('.customer-ugc-reviews__dot'));
      this.previousButton = this.querySelector('.customer-ugc-reviews__arrow--previous');
      this.nextButton = this.querySelector('.customer-ugc-reviews__arrow--next');
      this.status = this.querySelector('.customer-ugc-reviews__status');
      this.carouselEnabled = this.dataset.carouselEnabled === 'true';
      this.sidePeeks = this.dataset.sidePeeks === 'true';
      this.loop = this.dataset.loop === 'true' || this.sidePeeks;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion();
      this.autoplayInterval = Number(this.dataset.autoplayInterval) || 6000;
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.currentIndex = 0;
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.autoplayTimer = null;
      this.isPointerDown = false;
      this.hasDragged = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.setupReadMore();
      this.setupVideo();
      this.setupLightbox();

      if (!this.carouselEnabled || !this.track || this.slides.length < 1) return;

      this.setupSidePeekClones();

      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1, true));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.slideTo), true));
      });

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('click', (event) => {
        if (!this.hasDragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.hasDragged = false;
      }, true);

      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
      }
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      requestAnimationFrame(() => {
        this.updateScrollableState();
        this.goTo(0, false);
        this.updateControls(false);
        this.startAutoplay();
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.currentIndex = this.nearestSlideIndex();
        this.updateControls(false);
      });
    }

    onResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.updateScrollableState();
        this.goTo(this.currentIndex, false);
        this.setupReadMore();
      });
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('a, button, video, input, textarea, select')) return;

      this.isPointerDown = true;
      this.hasDragged = false;
      this.pointerStartX = event.clientX;
      this.scrollStartLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture?.(event.pointerId);
      this.stopAutoplay();
    }

    onPointerMove(event) {
      if (!this.isPointerDown) return;
      const distance = event.clientX - this.pointerStartX;
      if (Math.abs(distance) > 5) this.hasDragged = true;
      this.track.scrollLeft = this.scrollStartLeft - distance;
    }

    onPointerUp(event) {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;
      this.track.classList.remove('is-dragging');
      this.track.releasePointerCapture?.(event.pointerId);
      this.currentIndex = this.nearestSlideIndex();
      this.goTo(this.currentIndex, true);
      this.startAutoplay();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const selectedSlide = event.target.closest('.customer-ugc-reviews__slide');
      if (!selectedSlide) return;
      const index = this.slides.indexOf(selectedSlide);
      if (index >= 0) this.goTo(index, true);
    }

    nearestSlideIndex() {
      const paddingLeft = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      const trackStart = this.track.scrollLeft + paddingLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(trackStart - slide.offsetLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    }

    normalizeIndex(index) {
      if (this.loop) {
        if (index < 0) return this.slides.length - 1;
        if (index >= this.slides.length) return 0;
      }
      return Math.max(0, Math.min(index, this.slides.length - 1));
    }

    slideLeft(slide) {
      const paddingLeft = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      return slide.offsetLeft - paddingLeft;
    }

    setupSidePeekClones() {
      if (!this.sidePeeks || this.slides.length < 2) return;

      const prepareClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-slide-index');
        clone.removeAttribute('data-shopify-editor-block');
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('video').forEach((video) => {
          video.removeAttribute('controls');
          video.muted = true;
        });
        return clone;
      };

      this.leadingClone = prepareClone(this.slides[this.slides.length - 1]);
      this.trailingClone = prepareClone(this.slides[0]);
      this.track.prepend(this.leadingClone);
      this.track.append(this.trailingClone);
    }

    goTo(index, smooth) {
      if (!this.track || this.slides.length < 1) return;
      this.currentIndex = this.normalizeIndex(index);
      const slide = this.slides[this.currentIndex];
      this.track.scrollTo({
        left: this.slideLeft(slide),
        behavior: smooth && !reducedMotion() ? 'smooth' : 'auto'
      });
      this.updateControls(true);
      this.restartAutoplay();
    }

    updateScrollableState() {
      const canScroll = this.slides.length > 1 && this.track.scrollWidth > this.track.clientWidth + 2;
      this.classList.toggle('is-scrollable', canScroll);
      if (!canScroll) this.stopAutoplay();
    }

    updateControls(announce) {
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (!this.loop) {
        if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
        if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
      }

      if (announce && this.status) {
        this.status.textContent = `Review ${this.currentIndex + 1} of ${this.slides.length}`;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || !this.classList.contains('is-scrollable') || document.hidden) return;
      if (this.autoplayTimer) return;
      this.autoplayTimer = window.setInterval(() => {
        const nextIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
        this.goTo(nextIndex, true);
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      if (!this.autoplayTimer) return;
      this.stopAutoplay();
      this.startAutoplay();
    }

    setupReadMore() {
      this.querySelectorAll('.customer-ugc-reviews__body-wrap').forEach((wrap) => {
        const body = wrap.querySelector('.customer-ugc-reviews__body');
        const button = wrap.querySelector('.customer-ugc-reviews__read-more');
        if (!body || !button) return;

        body.classList.remove('is-expanded');
        button.setAttribute('aria-expanded', 'false');
        button.hidden = body.scrollHeight <= body.clientHeight + 2;

        if (button.dataset.bound === 'true') return;
        button.dataset.bound = 'true';
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          body.classList.toggle('is-expanded', !expanded);
        });
      });
    }

    setupVideo() {
      this.querySelectorAll('.customer-ugc-reviews__video').forEach((video) => {
        const customPoster = video.parentElement.dataset.customPoster;
        if (customPoster) video.poster = customPoster;
        const playIcon = video.parentElement.querySelector('.customer-ugc-reviews__play');
        if (!playIcon) return;
        video.addEventListener('play', () => { playIcon.hidden = true; });
        video.addEventListener('pause', () => { playIcon.hidden = false; });
      });
    }

    setupLightbox() {
      this.dialog = this.querySelector('.customer-ugc-reviews__lightbox');
      if (!this.dialog) return;

      this.lightboxImage = this.dialog.querySelector('.customer-ugc-reviews__lightbox-image');
      this.lightboxCaption = this.dialog.querySelector('.customer-ugc-reviews__lightbox-caption');
      this.lightboxPrevious = this.dialog.querySelector('.customer-ugc-reviews__lightbox-arrow--previous');
      this.lightboxNext = this.dialog.querySelector('.customer-ugc-reviews__lightbox-arrow--next');
      this.lightboxItems = [];
      this.lightboxIndex = 0;
      this.lastLightboxTrigger = null;
      this.previousBodyOverflow = '';

      this.querySelectorAll('[data-lightbox-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openLightbox(trigger));
      });
      this.dialog.querySelector('.customer-ugc-reviews__lightbox-close')?.addEventListener('click', () => this.dialog.close());
      this.lightboxPrevious?.addEventListener('click', () => this.showLightboxItem(this.lightboxIndex - 1));
      this.lightboxNext?.addEventListener('click', () => this.showLightboxItem(this.lightboxIndex + 1));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.body.style.overflow = this.previousBodyOverflow;
        this.lastLightboxTrigger?.focus();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
          return;
        }
        if (event.key === 'ArrowLeft') this.showLightboxItem(this.lightboxIndex - 1);
        if (event.key === 'ArrowRight') this.showLightboxItem(this.lightboxIndex + 1);
      });
    }

    openLightbox(trigger) {
      const reviewId = trigger.dataset.reviewId;
      this.lightboxItems = Array.from(this.querySelectorAll(`[data-lightbox-trigger][data-review-id="${CSS.escape(reviewId)}"]`));
      this.lightboxIndex = this.lightboxItems.indexOf(trigger);
      this.lastLightboxTrigger = trigger;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.showLightboxItem(this.lightboxIndex);
      this.dialog.showModal();
    }

    showLightboxItem(index) {
      if (index < 0 || index >= this.lightboxItems.length) return;
      this.lightboxIndex = index;
      const item = this.lightboxItems[index];
      const alt = item.dataset.lightboxAlt || '';
      this.lightboxImage.src = item.dataset.lightboxSrc;
      this.lightboxImage.alt = alt;
      this.lightboxCaption.textContent = alt;
      this.lightboxPrevious.disabled = index === 0;
      this.lightboxNext.disabled = index === this.lightboxItems.length - 1;
      const hideArrows = this.lightboxItems.length < 2;
      this.lightboxPrevious.hidden = hideArrows;
      this.lightboxNext.hidden = hideArrows;
    }
  }

  customElements.define('customer-ugc-reviews', CustomerUgcReviews);
})();
